import type { FC, ReactNode } from 'react'
import type { CallOutcome, MessageKind, ReminderLog } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { getFollowUpBucket, getStats } from '@/lib/clinic/selectors'
import { formatTimestamp, formatWeekday, formatTime } from '@/lib/clinic/dates'
import { Button, buttonVariants } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PatientAvatar } from '@/components/app/patient-avatar'
import { FollowUpBadge } from '@/components/app/status-badges'
import { SimpleSelect } from '@/components/app/common'
import { FormField } from '@/components/app/form-bits'

import {
    CalendarClock,
    Copy,
    MessageCircle,
    Phone,
    PhoneCall,
    Send
} from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    CALL_OUTCOME_LABELS,
    MESSAGE_KIND_LABELS,
    formatPhone,
    telHref
} from '@/lib/clinic/format'

import {
    activeMessageChannel,
    buildMessageValues,
    composeMessage,
    suggestTemplate
} from '@/lib/clinic/messaging'

export type ContactChannel = 'whatsapp' | 'call'

interface ReminderDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patientId: string
    followUpId: string | null
    initialChannel: ContactChannel
    onReschedule: (followUpId: string) => void
}

const MESSAGE_KINDS: MessageKind[] = ['reminder', 'overdue', 'confirmation', 'custom']

const OUTCOMES: CallOutcome[] = ['confirmed', 'no-answer', 'reschedule', 'call-back']

const historyLabel = (reminder: ReminderLog): string => {
    if (reminder.channel === 'call') return reminder.outcome ? `Call: ${CALL_OUTCOME_LABELS[reminder.outcome].toLowerCase()}` : 'Phone call'

    return `WhatsApp: ${MESSAGE_KIND_LABELS[reminder.template ?? 'custom'].toLowerCase()}`
}

export const ReminderDialog: FC<ReminderDialogProps> = ({
    open,
    onOpenChange,
    patientId,
    followUpId,
    initialChannel,
    onReschedule
}): ReactNode => {
    const { data, index, today, logReminder } = useClinic()
    const settings = data.settings
    const patient = index.patientsById.get(patientId) ?? null
    const followUp = followUpId
        ? index.followUpsById.get(followUpId) ?? null
        : patient ? getStats(index, patient.id).pendingFollowUp : null

    const values = useMemo(() => (patient ? buildMessageValues(patient, followUp, settings) : null), [patient, followUp, settings])

    const [channel, setChannel] = useState<ContactChannel>(initialChannel)
    const [kind, setKind] = useState<MessageKind>(() => suggestTemplate(followUp, today, settings))
    const [message, setMessage] = useState(() => (values ? composeMessage(kind, values, settings) : ''))
    const [outcome, setOutcome] = useState<CallOutcome | null>(null)
    const [note, setNote] = useState('')
    const [error, setError] = useState<string | null>(null)

    if (!patient || !values) return null

    const history = (followUp ? index.remindersByFollowUp.get(followUp.id) : index.remindersByPatient.get(patient.id)) ?? []

    const changeKind = (next: MessageKind): void => {
        setKind(next)
        setMessage(composeMessage(next, values, settings))
    }

    const openWhatsApp = (): void => {
        if (!message.trim()) {
            setError("Write a message first.")

            return
        }

        void activeMessageChannel.send({ phone: patient.phone, message, countryCode: settings.followUp.countryCode })

        logReminder({
            patientId: patient.id,
            followUpId: followUp?.id ?? null,
            channel: 'whatsapp',
            template: kind,
            message: message.trim(),
            outcome: null,
            note: ''
        })

        onOpenChange(false)

        toast.success('WhatsApp opened with your message', {
            description: 'Press send in WhatsApp to deliver it. This reminder is saved in the history.'
        })
    }

    const copyMessage = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(message)
            toast.success('Message copied')
        }
        catch {
            toast.error("Couldn't copy the message", { description: 'Select the text and copy it manually.' })
        }
    }

    const saveCall = (): void => {
        if (!outcome) {
            setError("Choose how the call went.")

            return
        }

        logReminder({
            patientId: patient.id,
            followUpId: followUp?.id ?? null,
            channel: 'call',
            template: null,
            message: '',
            outcome,
            note
        })

        onOpenChange(false)

        if (outcome === 'reschedule' && followUp) {
            toast.success('Call saved', { description: 'Choose the new returning date.' })
            onReschedule(followUp.id)

            return
        }

        toast.success('Call saved', {
            description: outcome === 'confirmed' ? 'The follow-up is marked as confirmed.' : CALL_OUTCOME_LABELS[outcome]
        })
    }

    return (
        <Dialog open={open} onOpenChange={next => onOpenChange(next)}>
            <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-lg'>
                <DialogHeader>
                    <DialogTitle className='text-lg'>
                        Contact {patient.name}
                    </DialogTitle>

                    <DialogDescription>
                        Send a WhatsApp reminder or call the patient, then save what happened.
                    </DialogDescription>
                </DialogHeader>

                <div className='flex items-center gap-3 rounded-xl border bg-muted/40 p-3'>
                    <PatientAvatar name={patient.name} seed={patient.id} />

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-semibold text-foreground'>
                            {patient.name}
                            <span className='font-normal text-muted-foreground'> · {patient.id}</span>
                        </p>

                        <p className='truncate text-xs text-muted-foreground'>
                            {formatPhone(patient.phone)}
                        </p>
                    </div>

                    {followUp && followUp.status === 'pending' && (
                        <div className='hidden text-right sm:block'>
                            <FollowUpBadge bucket={getFollowUpBucket(followUp, today)} />

                            <p className='mt-1 text-xs text-muted-foreground'>
                                {formatWeekday(followUp.dueDate)}
                                {followUp.dueTime ? `, ${formatTime(followUp.dueTime)}` : ''}
                            </p>
                        </div>
                    )}
                </div>

                <Tabs value={channel} onValueChange={value => setChannel(value as ContactChannel)} className='gap-4'>
                    <TabsList className='w-full'>
                        <TabsTrigger value='whatsapp'>
                            <MessageCircle aria-hidden='true' />
                            WhatsApp
                        </TabsTrigger>

                        <TabsTrigger value='call'>
                            <Phone aria-hidden='true' />
                            Phone call
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value='whatsapp' className='grid gap-4'>
                        <FormField label='Message template' htmlFor='reminder-template'>
                            <SimpleSelect
                                id='reminder-template'
                                value={kind}
                                onValueChange={changeKind}
                                options={MESSAGE_KINDS.map(item => ({ value: item, label: MESSAGE_KIND_LABELS[item] }))}
                            />
                        </FormField>

                        <FormField
                            label='Message'
                            htmlFor='reminder-message'
                            hint='You can edit the text before sending. WhatsApp opens with it ready, and you press send there.'
                            error={error}
                        >
                            <Textarea
                                id='reminder-message'
                                value={message}
                                onChange={event => {
                                    setMessage(event.target.value)
                                    setError(null)
                                }}
                                rows={5}
                                className='min-h-28 leading-relaxed'
                            />
                        </FormField>

                        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
                            <Button type='button' variant='outline' onClick={() => void copyMessage()}>
                                <Copy aria-hidden='true' />
                                Copy text
                            </Button>

                            <Button type='button' onClick={openWhatsApp} className='bg-emerald-600 shadow-emerald-700/20 hover:bg-emerald-700'>
                                <Send aria-hidden='true' />
                                Open WhatsApp
                            </Button>
                        </div>
                    </TabsContent>

                    <TabsContent value='call' className='grid gap-4'>
                        <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3'>
                            <div>
                                <p className='text-xs font-medium text-muted-foreground'>
                                    Phone number
                                </p>

                                <p className='font-heading text-xl font-semibold tracking-wide text-foreground tabular-nums'>
                                    {formatPhone(patient.phone)}
                                </p>
                            </div>

                            <a href={telHref(patient.phone)} className={buttonVariants({ size: 'lg' })}>
                                <PhoneCall aria-hidden='true' />
                                Call now
                            </a>
                        </div>

                        <fieldset className='grid gap-2'>
                            <legend className='mb-1.5 text-[0.8rem] font-medium text-foreground'>
                                How did the call go?
                            </legend>

                            <div className='grid grid-cols-2 gap-2'>
                                {OUTCOMES.map(item => (
                                    <button
                                        key={item}
                                        type='button'
                                        aria-pressed={outcome === item}
                                        onClick={() => {
                                            setOutcome(item)
                                            setError(null)
                                        }}
                                        className={cn(
                                            'rounded-xl border bg-white px-3 py-2.5 text-left text-sm font-medium transition-colors outline-none hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/50',
                                            outcome === item && 'border-primary/40 bg-accent text-accent-foreground hover:bg-accent'
                                        )}
                                    >
                                        {CALL_OUTCOME_LABELS[item]}
                                    </button>
                                ))}
                            </div>
                        </fieldset>

                        <FormField label='Call note' htmlFor='call-note' error={error} hint='Optional, for example the time the patient prefers.'>
                            <Textarea id='call-note' value={note} onChange={event => setNote(event.target.value)} rows={2} />
                        </FormField>

                        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>

                            <Button type='button' onClick={saveCall}>
                                {outcome === 'reschedule' ? (
                                    <>
                                        <CalendarClock aria-hidden='true' />
                                        Save and pick a new date
                                    </>
                                ) : 'Save call'}
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>

                {history.length > 0 && (
                    <div className='grid gap-2 border-t pt-4'>
                        <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                            Contact history
                        </p>

                        <ul className='grid gap-2'>
                            {history.slice(0, 4).map(reminder => (
                                <li key={reminder.id} className='flex items-start gap-2.5 text-sm'>
                                    <span className={cn(
                                        'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full',
                                        reminder.channel === 'whatsapp' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'
                                    )}>
                                        {reminder.channel === 'whatsapp' ? <MessageCircle className='size-3.5' /> : <Phone className='size-3.5' />}
                                    </span>

                                    <div className='min-w-0'>
                                        <p className='font-medium text-foreground'>
                                            {historyLabel(reminder)}
                                        </p>

                                        <p className='text-xs text-muted-foreground'>
                                            {formatTimestamp(reminder.createdAt)}
                                            {reminder.note ? ` · ${reminder.note}` : ''}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
